import type {
  IssueWorkflowOutDto,
  WorkflowCreateInDto,
  WorkflowUpdateInDto,
  WorkflowStageOutDto,
  StageCreateInDto,
  StageUpdateInDto,
  StageReorderInDto,
  WorkflowDecisionInDto,
  WorkflowDecisionOutDto,
} from 'shared/domain/workflow/types';
import type { EntityCollectionActions } from '../entityCollection';
import type { StoreData, ActionsState, DataActions, AsyncAction } from '../types';
import type { DecisionValidationError } from 'shared/domain/workflow/validation';

export const workflowActionNames = [
  'createWorkflow',
  'updateWorkflow',
  'createStage',
  'updateStage',
  'deleteStage',
  'reorderStage',
  'createDecision',
] as const;

export type WorkflowActionName = (typeof workflowActionNames)[number];

export type WorkflowActions = DataActions<IssueWorkflowOutDto> & EntityCollectionActions<IssueWorkflowOutDto>;

/** Each change resolves with what the server saved and rejects with its error. */
export type WorkflowAsyncActions = {
  createWorkflow: (dto: WorkflowCreateInDto, projectId: string) => Promise<IssueWorkflowOutDto>;
  updateWorkflow: (workflowId: string, data: WorkflowUpdateInDto, projectId: string) => Promise<IssueWorkflowOutDto>;
  createStage: (workflowId: string, data: StageCreateInDto, projectId: string) => Promise<WorkflowStageOutDto>;
  updateStage: (workflowId: string, stageId: string, data: StageUpdateInDto, projectId: string) => Promise<WorkflowStageOutDto>;
  deleteStage: (workflowId: string, stageId: string, projectId: string) => Promise<WorkflowStageOutDto>;
  reorderStage: (workflowId: string, stageId: string, data: StageReorderInDto, projectId: string) => Promise<WorkflowStageOutDto>;
  createDecision: (projectId: string, issueId: string, data: WorkflowDecisionInDto) => Promise<WorkflowDecisionOutDto>;
};

export type WorkflowStore = {
  data: StoreData<IssueWorkflowOutDto>;
  actions: WorkflowActions;
  asyncActions: WorkflowAsyncActions;
  _actionStates: ActionsState<WorkflowActionName>;
};

export type UseWorkflowsResult = {
  workflows: IssueWorkflowOutDto[];
  loading: boolean;
  reload: WorkflowActions['reload'];
  createWorkflow: AsyncAction<[dto: WorkflowCreateInDto, projectId: string], IssueWorkflowOutDto>;
  updateWorkflow: AsyncAction<[workflowId: string, data: WorkflowUpdateInDto, projectId: string], IssueWorkflowOutDto>;
  createStage: AsyncAction<[workflowId: string, data: StageCreateInDto, projectId: string], WorkflowStageOutDto>;
  updateStage: AsyncAction<[workflowId: string, stageId: string, data: StageUpdateInDto, projectId: string], WorkflowStageOutDto>;
  deleteStage: AsyncAction<[workflowId: string, stageId: string, projectId: string], WorkflowStageOutDto>;
  reorderStage: AsyncAction<[workflowId: string, stageId: string, data: StageReorderInDto, projectId: string], WorkflowStageOutDto>;
};

export type UseWorkflowDecisionResult = {
  createDecision: AsyncAction<
    [projectId: string, issueId: string, data: WorkflowDecisionInDto],
    WorkflowDecisionOutDto
  >;
  validateInput: (data: WorkflowDecisionInDto) => DecisionValidationError[];
};
